import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { authApi, User } from '../api/auth';
import { clearCachedPrivateKey } from '../utils/e2eeClient';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (
    email: string,
    password: string,
    e2ee: {
      public_jwk: string;
      encrypted_private_jwk: string;
      kdf_salt_b64: string;
      kdf_iterations: number;
      kdf_hash: string;
      priv_enc_alg: string;
      priv_enc_iv_b64: string;
    },
    fullName?: string
  ) => Promise<void>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      authApi.getMe()
        .then(setUser)
        .catch(() => {
          localStorage.removeItem('token');
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const response = await authApi.login({ username: email, password });
    localStorage.setItem('token', response.access_token);
    const userData = await authApi.getMe();
    setUser(userData);
  };

  const register = async (
    email: string,
    password: string,
    e2ee: {
      public_jwk: string;
      encrypted_private_jwk: string;
      kdf_salt_b64: string;
      kdf_iterations: number;
      kdf_hash: string;
      priv_enc_alg: string;
      priv_enc_iv_b64: string;
    },
    fullName?: string
  ) => {
    await authApi.register({ email, password, full_name: fullName, ...e2ee });
  };

  const logout = () => {
    localStorage.removeItem('token');
    clearCachedPrivateKey();
    setUser(null);
  };

  const refreshUser = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setUser(null);
      return;
    }
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch {
      localStorage.removeItem('token');
      setUser(null);
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
